/* Sales Schedule — phone-first, iPad week grid.
 *
 * Built for a rep standing on a site with one thumb free and patchy 4G:
 *   · phone → ONE day at a time, swipe left/right to change day
 *   · iPad  → seven day-columns side by side
 *   · every control is at least 48px so it can be hit without looking
 *   · the only request is /sales/schedule, which carries job summaries only.
 *     Customers and mixes are fetched lazily, and only if Book is opened.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { usePlant } from "@/context/PlantContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, RefreshCw, Plus, CalendarDays, Droplets, Truck, Loader2, X } from "lucide-react";

const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (s, n) => { const d = new Date(s + "T00:00:00"); d.setDate(d.getDate() + n); return iso(d); };
const startOfWeek = (s) => { const d = new Date(s + "T00:00:00"); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return iso(d); };
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const longDate = (s) => { const d = new Date(s + "T00:00:00"); return `${DOW[(d.getDay() + 6) % 7]} ${d.getDate()} ${MON[d.getMonth()]}`; };

const BAND = {
  available: { label: "Available", chip: "bg-emerald-500 text-black border-emerald-400", dot: "bg-emerald-500" },
  filling: { label: "Filling Up", chip: "bg-amber-400 text-black border-amber-300 animate-pulse", dot: "bg-amber-400 animate-pulse" },
  booked: { label: "Fully Booked", chip: "bg-red-600 text-white border-red-500", dot: "bg-red-600" },
};

const STATUS_TONE = {
  scheduled: "border-zinc-700 text-zinc-300",
  dispatched: "border-sky-600 text-sky-400",
  in_progress: "border-primary-orange text-primary-orange",
  batching: "border-primary-orange text-primary-orange",
  complete: "border-emerald-700 text-emerald-500",
  completed: "border-emerald-700 text-emerald-500",
};

export const CapacityBadge = ({ band, pct, size = "sm" }) => {
  const b = BAND[band] || BAND.available;
  return (
    <span data-testid={`sales-band-${band}`}
      className={`mono uppercase tracking-widest font-black border inline-flex items-center gap-1.5 ${b.chip} ${size === "lg" ? "text-xs px-3 py-1.5" : "text-[10px] px-2 py-1"}`}>
      {b.label}{pct != null && <span className="opacity-70">{Math.round(pct)}%</span>}
    </span>
  );
};

const JobCard = ({ j }) => (
  <div data-testid={`sales-job-${j.id}`} className="border border-industrial bg-surface-2 p-3 active:bg-zinc-800 transition-colors">
    <div className="flex items-start gap-2">
      <div className="mono text-lg font-black text-primary-orange leading-none w-14 shrink-0">{j.time || "—"}</div>
      <div className="min-w-0 flex-1">
        <div className="font-bold text-sm truncate text-ink">{j.customer_name}</div>
        {j.site && <div className="text-xs text-muted-industrial truncate">{j.site}</div>}
      </div>
      <div className="text-right shrink-0">
        <div className="mono text-base font-black text-ink leading-none">{j.quantity_m3}<span className="text-[10px] ml-0.5">m³</span></div>
        {j.delivered_m3 > 0 && <div className="mono text-[10px] text-emerald-500">{j.delivered_m3} done</div>}
      </div>
    </div>
    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
      {j.mix_code && <span className="mono text-[10px] uppercase px-1.5 py-0.5 border border-industrial text-muted-industrial">{j.mix_code}</span>}
      <span className={`mono text-[10px] uppercase px-1.5 py-0.5 border ${STATUS_TONE[j.status] || STATUS_TONE.scheduled}`}>{(j.status || "").replace("_", " ")}</span>
      {j.pump && <span className="mono text-[10px] uppercase px-1.5 py-0.5 border border-sky-700 text-sky-400 inline-flex items-center gap-1"><Droplets className="w-2.5 h-2.5" />pump</span>}
      {j.cash_sale && <span className="mono text-[10px] uppercase px-1.5 py-0.5 border border-amber-700 text-amber-400">eft</span>}
    </div>
    {j.note && <div className="text-[11px] text-muted-industrial mt-1.5 line-clamp-2">{j.note}</div>}
  </div>
);

const DayHeader = ({ day, capacity }) => {
  const b = BAND[day.band] || BAND.available;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="mono text-[11px] uppercase tracking-widest text-muted-industrial">{longDate(day.date)}</div>
        <CapacityBadge band={day.band} pct={day.utilisation_pct} />
      </div>
      <div className="h-2 bg-zinc-800 relative overflow-hidden">
        <div className={`absolute inset-y-0 left-0 transition-[width] duration-500 ${b.dot}`} style={{ width: `${Math.min(100, day.utilisation_pct)}%` }} />
      </div>
      <div className="flex justify-between mono text-[10px] text-muted-industrial">
        <span><span className="text-ink font-bold">{day.booked_m3}</span> of {capacity} m³ booked</span>
        <span>{day.job_count} job{day.job_count === 1 ? "" : "s"} · {day.remaining_m3} m³ left</span>
      </div>
    </div>
  );
};

export default function SalesSchedule() {
  const { plantId } = usePlant();
  const today = iso(new Date());
  const [date, setDate] = useState(today);
  // iPad and up open on the week grid; phones stay on one day. Read synchronously so
  // there's only ever ONE request — setting it in an effect fired a second, and the
  // slower response could land last and show a single day in the week view.
  const [week, setWeek] = useState(() => typeof window !== "undefined" && window.innerWidth >= 768);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [slide, setSlide] = useState("");           // entrance direction for the swipe

  const start = week ? startOfWeek(date) : date;
  const span = week ? 7 : 1;

  const reqRef = useRef(0);
  const load = useCallback(async () => {
    const myReq = ++reqRef.current;
    setLoading(true);
    try {
      const q = new URLSearchParams({ start, days: String(span) });
      if (plantId) q.set("plant_id", plantId);
      const { data } = await api.get(`/sales/schedule?${q}`);
      if (myReq !== reqRef.current) return;         // a newer request already won
      setData(data);
    } catch (e) {
      if (myReq === reqRef.current) toast.error("Couldn't load the schedule");
    } finally { if (myReq === reqRef.current) setLoading(false); }
  }, [start, span, plantId]);
  useEffect(() => { load(); }, [load]);

  const go = useCallback((n) => {
    setSlide(n > 0 ? "left" : "right");
    setDate((d) => addDays(d, week ? n * 7 : n));
  }, [week]);

  // Swipe to change date — the sales team flick between today and tomorrow all day.
  const touch = useRef(null);
  const onStart = (e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onEnd = (e) => {
    if (!touch.current) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    touch.current = null;
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;   // ignore scrolls
    go(dx < 0 ? 1 : -1);
  };
  useEffect(() => {
    const k = (e) => { if (e.key === "ArrowLeft") go(-1); if (e.key === "ArrowRight") go(1); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [go]);

  const days = data?.days || [];
  const capacity = data?.capacity_m3 || 0;
  const weekTotal = useMemo(() => days.reduce((s, d) => s + d.booked_m3, 0), [days]);

  return (
    <div className="space-y-4 pb-28 md:pb-6" data-testid="sales-schedule-page">
      {/* header */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="label-tag">Sales Schedule</div>
          <h1 className="heading text-3xl sm:text-4xl font-black uppercase leading-none" data-testid="sales-title">
            {week ? "Week View" : (date === today ? "Today" : longDate(date))}
          </h1>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" data-testid="sales-view-toggle" onClick={() => setWeek((w) => !w)}
            className="h-12 px-4 border border-industrial mono text-[11px] uppercase tracking-widest hover:border-primary-orange hover:text-primary-orange inline-flex items-center gap-2">
            <CalendarDays className="w-4 h-4" />{week ? "Day" : "Week"}
          </button>
          <button type="button" data-testid="sales-refresh" onClick={load} title="Refresh"
            className="h-12 w-12 border border-industrial inline-flex items-center justify-center hover:border-primary-orange hover:text-primary-orange">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* thumb-sized date nav */}
      <div className="flex items-stretch gap-2">
        <button type="button" data-testid="sales-prev" onClick={() => go(-1)} aria-label="Previous"
          className="h-14 w-14 shrink-0 border border-industrial inline-flex items-center justify-center active:bg-primary-orange active:text-black hover:border-primary-orange">
          <ChevronLeft className="w-7 h-7" />
        </button>
        <button type="button" data-testid="sales-today" onClick={() => setDate(today)}
          className={`h-14 flex-1 border mono uppercase tracking-widest text-xs font-black ${date === today ? "border-primary-orange text-primary-orange" : "border-industrial text-muted-industrial"}`}>
          {date === today ? "Today" : "Jump to Today"}
        </button>
        <button type="button" data-testid="sales-next" onClick={() => go(1)} aria-label="Next"
          className="h-14 w-14 shrink-0 border border-industrial inline-flex items-center justify-center active:bg-primary-orange active:text-black hover:border-primary-orange">
          <ChevronRight className="w-7 h-7" />
        </button>
      </div>

      {/* legend + week total */}
      <div className="flex items-center gap-2 flex-wrap mono text-[10px] uppercase tracking-widest text-muted-industrial">
        <CapacityBadge band="available" /><CapacityBadge band="filling" /><CapacityBadge band="booked" />
        <span className="ml-auto inline-flex items-center gap-1"><Truck className="w-3 h-3" />{data?.capacity_trucks_available ?? "—"} trucks · {capacity} m³/day
          {week && <> · <span className="text-ink font-bold">{weekTotal.toFixed(1)} m³ this week</span></>}</span>
      </div>

      {/* schedule */}
      <div onTouchStart={onStart} onTouchEnd={onEnd} key={start + span}
        className={`${slide === "left" ? "animate-in slide-in-from-right-6" : slide === "right" ? "animate-in slide-in-from-left-6" : ""} duration-200`}>
        {loading && !data && <div className="mono text-xs uppercase text-muted-industrial py-10 text-center">Loading…</div>}

        {!week && days[0] && (
          <div className="space-y-3" data-testid="sales-day-view">
            <DayHeader day={days[0]} capacity={capacity} />
            {days[0].jobs.length === 0
              ? <div className="border border-dashed border-industrial p-8 text-center mono text-[11px] uppercase tracking-widest text-muted-industrial" data-testid="sales-empty">
                  Nothing booked — the whole day is open
                </div>
              : <div className="space-y-2">{days[0].jobs.map((j) => <JobCard key={j.id} j={j} />)}</div>}
          </div>
        )}

        {week && (
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2" data-testid="sales-week-view">
            {days.map((d) => (
              <button key={d.date} type="button" onClick={() => { setDate(d.date); setWeek(false); }}
                data-testid={`sales-week-day-${d.date}`}
                className={`text-left border p-2 space-y-2 min-h-[150px] hover:border-primary-orange ${d.date === today ? "border-primary-orange" : "border-industrial"}`}>
                <div className="flex items-center justify-between">
                  <span className="mono text-[11px] uppercase tracking-widest text-ink font-bold">{longDate(d.date)}</span>
                  <span className={`w-2.5 h-2.5 rounded-full ${(BAND[d.band] || BAND.available).dot}`} />
                </div>
                <div className="mono text-xl font-black text-ink leading-none">{d.booked_m3}<span className="text-[10px] ml-0.5">m³</span></div>
                <div className="h-1.5 bg-zinc-800 relative"><div className={`absolute inset-y-0 left-0 ${(BAND[d.band] || BAND.available).dot}`} style={{ width: `${Math.min(100, d.utilisation_pct)}%` }} /></div>
                <div className="space-y-1">
                  {d.jobs.slice(0, 4).map((j) => (
                    <div key={j.id} className="mono text-[10px] text-muted-industrial truncate"><span className="text-primary-orange">{j.time || "—"}</span> {j.customer_name}</div>
                  ))}
                  {d.jobs.length > 4 && <div className="mono text-[10px] text-muted-industrial">+{d.jobs.length - 4} more</div>}
                  {d.jobs.length === 0 && <div className="mono text-[10px] text-zinc-600 uppercase">open</div>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* thumb-reach Book button */}
      <button type="button" data-testid="sales-book-btn" onClick={() => setBooking(true)}
        className="fixed bottom-5 right-5 md:static md:w-full z-40 h-16 md:h-14 px-7 bg-primary-orange text-black mono uppercase tracking-widest font-black text-sm inline-flex items-center justify-center gap-2 shadow-2xl active:scale-95 transition-transform">
        <Plus className="w-6 h-6" /> Book
      </button>

      {booking && <BookSheet date={date} onClose={() => setBooking(false)} onBooked={() => { setBooking(false); load(); }} />}
    </div>
  );
}

/* Compact booking sheet. Customers and mixes are only fetched when this opens, so
 * the schedule itself stays cheap on mobile data. */
function BookSheet({ date, onClose, onBooked }) {
  const { plantId } = usePlant();
  const [refs, setRefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ customer_id: "", mix_id: "", quantity_m3: "", date, time_required: "07:00", site_name: "", site_contact_phone: "", notes: "" });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => { (async () => {
    try {
      const [c, m] = await Promise.all([api.get("/customers"), api.get("/mixes")]);
      setRefs({ customers: c.data || [], mixes: m.data || [] });
    } catch { toast.error("Couldn't load customers and mixes"); }
  })(); }, []);

  const submit = async () => {
    if (!f.customer_id || !f.mix_id || !Number(f.quantity_m3)) return toast.error("Customer, mix and m³ are all required");
    setSaving(true);
    try {
      await api.post("/jobs", {
        customer_id: f.customer_id, mix_id: f.mix_id, quantity_m3: Number(f.quantity_m3),
        date: f.date, time_required: f.time_required, site_name: f.site_name,
        site_contact_phone: f.site_contact_phone, notes: f.notes,
        ...(plantId ? { plant_id: plantId } : {}),
      });
      toast.success("Job booked");
      onBooked();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't book the job");
    } finally { setSaving(false); }
  };

  const field = "h-14 w-full bg-surface-2 border border-industrial px-3 text-base text-ink focus:border-primary-orange outline-none";
  const lbl = "mono text-[10px] uppercase tracking-widest text-muted-industrial";

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end md:items-center justify-center" data-testid="sales-book-sheet">
      <div className="w-full md:max-w-lg bg-industrial border-t md:border border-industrial max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-industrial sticky top-0 bg-industrial">
          <h2 className="heading text-2xl font-black uppercase">Book a Job</h2>
          <button type="button" onClick={onClose} data-testid="sales-book-close" className="h-12 w-12 inline-flex items-center justify-center border border-industrial"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          {!refs ? <div className="mono text-xs uppercase text-muted-industrial py-8 text-center">Loading customers…</div> : (
            <>
              <div><div className={lbl}>Customer</div>
                <select data-testid="sales-book-customer" className={field} value={f.customer_id} onChange={set("customer_id")}>
                  <option value="">— pick a customer —</option>
                  {refs.customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><div className={lbl}>Mix</div>
                  <select data-testid="sales-book-mix" className={field} value={f.mix_id} onChange={set("mix_id")}>
                    <option value="">— mix —</option>
                    {refs.mixes.map((m) => <option key={m.id} value={m.id}>{m.code}</option>)}
                  </select></div>
                <div><div className={lbl}>Quantity m³</div>
                  <input data-testid="sales-book-qty" className={field} type="number" inputMode="decimal" step="0.5" value={f.quantity_m3} onChange={set("quantity_m3")} placeholder="7.0" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><div className={lbl}>Date</div>
                  <input data-testid="sales-book-date" className={field} type="date" value={f.date} onChange={set("date")} /></div>
                <div><div className={lbl}>On site by</div>
                  <input data-testid="sales-book-time" className={field} type="time" value={f.time_required} onChange={set("time_required")} /></div>
              </div>
              <div><div className={lbl}>Site</div>
                <input data-testid="sales-book-site" className={field} value={f.site_name} onChange={set("site_name")} placeholder="12 Smith St" /></div>
              <div><div className={lbl}>Site contact mobile</div>
                <input data-testid="sales-book-phone" className={field} type="tel" inputMode="tel" value={f.site_contact_phone} onChange={set("site_contact_phone")} placeholder="04.." /></div>
              <div><div className={lbl}>Notes</div>
                <input data-testid="sales-book-notes" className={field} value={f.notes} onChange={set("notes")} placeholder="Pump on site, tight access…" /></div>
              <button type="button" data-testid="sales-book-submit" onClick={submit} disabled={saving}
                className="h-16 w-full bg-primary-orange text-black mono uppercase tracking-widest font-black text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />} Book It
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
